<template>
  <!-- The tenant's side of the sign-in page. Every colour is a tenant token:
       the deep background (branding secondary), the lines, tiles and eyebrow
       mark (branding tertiary). Decorative layers are aria-hidden. -->
  <section
    class="si-panel"
    :class="{ 'si-panel--light': light }"
    :style="{ '--si-deep': bare ? 'transparent' : deep }"
    data-test="field-panel"
  >
    <div v-if="bare" hidden></div>
    <div v-else-if="scene === 'network'" class="si-panel__scene" aria-hidden="true">
      <PanelNetwork :labels="networkNodes" :mark-url="branding?.logoLightUrl || branding?.logoDarkUrl || null" :brand="brand" />
    </div>
    <svg v-else class="si-panel__contour" viewBox="0 0 800 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <path
        v-for="(line, i) in CONTOURS"
        :key="i"
        :d="line"
        :class="i % 2 === 0 ? 'si-panel__line--accent' : 'si-panel__line--soft'"
      />
    </svg>
    <div v-if="!bare && scene !== 'network'" class="si-panel__scan" aria-hidden="true"></div>
    <div v-if="!bare" class="si-panel__glow" aria-hidden="true"></div>

    <!-- Bare (metaship.ai): the plain mark, black on the light theme and white on the dark theme
         (no white tile), and the brand row links back to the landing page. -->
    <a v-if="bare" class="si-panel__brand si-panel__brand--link" href="https://metaship.ai/" data-test="signin-home-link">
      <img v-if="markUrl" class="si-panel__mark" :src="markUrl" alt="" width="40" height="40" />
      <LogoTile v-else :branding="branding" :alt="brand" :size="40" />
      <span class="si-panel__name">{{ brand }}</span>
    </a>
    <header v-else class="si-panel__brand">
      <LogoTile :branding="branding" :alt="brand" :size="40" />
      <span class="si-panel__name">{{ brand }}</span>
    </header>

    <div class="si-panel__copy">
      <p class="si-panel__eyebrow">{{ eyebrow }}</p>
      <!-- Bare (metaship.ai): the last line carries the landing-page brand gradient, as the hero h1 does. -->
      <h1 v-if="bare && accent.text" class="si-panel__headline">{{ accent.lead }}<br v-if="accent.block" /><template v-else>{{ " " }}</template><em class="si-panel__em">{{ accent.text }}</em></h1>
      <h1 v-else class="si-panel__headline">{{ headline }}</h1>
      <p v-if="lede" class="si-panel__lede">{{ lede }}</p>
    </div>

    <div class="si-panel__foot">
      <div v-if="features.length" class="si-panel__inside">
        <p class="si-panel__inside-label">{{ insideLabel }}</p>
        <ul class="si-panel__features">
          <li v-for="feature in features" :key="feature.title" class="si-panel__feature">
            <span class="si-panel__feature-icon" aria-hidden="true">
              <v-icon size="20">{{ feature.icon }}</v-icon>
            </span>
            <span class="si-panel__feature-copy">
              <span class="si-panel__feature-title">{{ feature.title }}</span>
              <span v-if="feature.description" class="si-panel__feature-text">{{ feature.description }}</span>
            </span>
          </li>
        </ul>
      </div>
      <p v-if="footnote" class="si-panel__footnote">{{ footnote }}</p>
    </div>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import type { TenantBranding } from "@tenant-theme";
import LogoTile from "@/shell/LogoTile.vue";
import PanelNetwork from "./PanelNetwork.vue";
import type { PanelScene } from "@/tenant/types";

/** Nine contour lines across an 800 × 900 field; the stroke dashes drift along them. */
const CONTOURS = [
  "M-40 120 C 160 60 320 180 520 120 S 860 40 900 110",
  "M-40 200 C 140 150 340 260 540 200 S 860 130 900 190",
  "M-40 290 C 180 240 360 350 560 280 S 860 220 900 270",
  "M-40 380 C 120 330 380 450 580 370 S 860 300 900 360",
  "M-40 470 C 160 420 400 540 600 460 S 860 400 900 450",
  "M-40 560 C 200 520 420 630 620 550 S 860 490 900 540",
  "M-40 650 C 180 600 440 720 640 640 S 860 580 900 630",
  "M-40 740 C 220 700 460 810 660 730 S 860 670 900 720",
  "M-40 830 C 200 790 480 900 680 820 S 860 760 900 810",
];

/** One line in the "what you get" list: a Material icon, a title, an optional line of text. */
export interface PanelFeature {
  icon: string;
  title: string;
  description?: string | null;
}

/* The 36-tile pulse grid was removed: it was decoration with no meaning and
   took the space that now holds the feature list.
const CELLS = Array.from({ length: 36 }, (_, index) => ({
  index,
  accent: (index * 7) % 5 === 0,
  delay: `${((index * 37) % 32) / 10}s`,
}));
*/

export default defineComponent({
  name: "FieldPanel",
  components: { LogoTile, PanelNetwork },
  props: {
    brand: { type: String, required: true },
    branding: { type: Object as PropType<TenantBranding | null>, default: null },
    /** The panel background. A CSS colour; the parent makes sure white text reads on it. */
    deep: { type: String, required: true },
    eyebrow: { type: String, required: true },
    headline: { type: String, required: true },
    lede: { type: String, default: "" },
    /** At most 3 lines; the parent picks the tenant's App Home tiles or the generic facts. */
    features: { type: Array as PropType<PanelFeature[]>, default: () => [] },
    insideLabel: { type: String, default: "" },
    /** A short line at the foot of the panel, e.g. the copyright. */
    footnote: { type: String, default: "" },
    /** The animated background: "contours" (default) or "network" (PanelNetwork.vue). */
    scene: { type: String as PropType<PanelScene>, default: "contours" },
    /** The "network" scene's party names; empty → the default six. */
    networkNodes: { type: Array as PropType<string[]>, default: () => [] },
    /** Dark ink, for a light page behind the panel. */
    light: { type: Boolean, default: false },
    /** No ground and no scene: the page draws the background (metaship.ai sign-in). */
    bare: { type: Boolean, default: false },
  },
  setup(props) {
    // The accent: the last line when the headline has line breaks, else the last word.
    const accent = computed(() => {
      const text = props.headline.trim();
      const nl = text.lastIndexOf("\n");
      if (nl > 0) return { lead: text.slice(0, nl), text: text.slice(nl + 1).trim(), block: true };
      const sp = text.lastIndexOf(" ");
      if (sp > 0) return { lead: text.slice(0, sp), text: text.slice(sp + 1), block: false };
      return { lead: "", text: "", block: false };
    });
    // The mark for the bare brand row. CSS turns it black or white, so either variant works.
    const markUrl = computed(() => props.branding?.logoLightUrl || props.branding?.logoDarkUrl || null);
    return { CONTOURS, accent, markUrl };
  },
});
</script>

<style scoped>
.si-panel {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 32px;
  flex: 56 1 0;
  min-width: 0;
  min-height: 560px;
  padding: max(48px, env(safe-area-inset-top, 0px)) 56px 48px max(56px, env(safe-area-inset-left, 0px));
  box-sizing: border-box;
  /* The ink is a variable so the light panel (--light) can turn every line and text dark. */
  --si-ink: 255, 255, 255;
  background: var(--si-deep);
  color: rgb(var(--si-ink));
}
.si-panel--light {
  --si-ink: 17, 25, 39;
}
/* The landing-page hero h1 accent: one flat blue per theme. Was a gradient clipped to the
   text (background-clip: text); removed 2026-11-01, gradient text reads as generated filler. */
.si-panel__em {
  font-style: normal;
  color: #6fb0e6;
}
.si-panel--light .si-panel__em {
  color: #2e6fae;
}
.si-panel__contour {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.4;
}
.si-panel__contour path {
  fill: none;
  stroke-width: 1.2;
  stroke-dasharray: 6 10;
}
.si-panel__line--accent {
  stroke: var(--tenant-tertiary);
  stroke-opacity: 0.6;
}
.si-panel__line--soft {
  stroke: rgb(var(--si-ink));
  stroke-opacity: 0.3;
}
/* The network scene sits to the right of the copy, behind it, like the landing-page watermark. */
.si-panel__scene {
  position: absolute;
  top: 50%;
  right: -6%;
  width: min(640px, 62%);
  transform: translateY(-50%);
  opacity: 0.95;
  pointer-events: none;
}
.si-panel__scan {
  position: absolute;
  inset: 0 0 auto;
  height: 160px;
  background: linear-gradient(180deg, transparent, var(--tenant-tertiary), transparent);
  opacity: 0.08;
  pointer-events: none;
}
.si-panel__brand,
.si-panel__copy,
.si-panel__foot {
  position: relative;
}
.si-panel__brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.si-panel__brand--link {
  align-self: flex-start;
  color: inherit;
  text-decoration: none;
  border-radius: 8px;
}
.si-panel__brand--link:focus-visible {
  outline: 2px solid rgb(var(--si-ink));
  outline-offset: 4px;
}
/* White mark on the dark theme, black mark on the light theme. */
.si-panel__mark {
  display: block;
  width: 40px;
  height: 40px;
  object-fit: contain;
  filter: brightness(0) invert(1);
}
.si-panel--light .si-panel__mark {
  filter: brightness(0);
}
.si-panel__name {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.3px;
}
.si-panel__copy {
  max-width: 520px;
}
/* White text, not the accent: an accent from the tenant palette is not sure
   to reach 4.5:1 on the deep colour. The accent shows as the bar before it. */
.si-panel__eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 16px;
  font-family: var(--si-mono);
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(var(--si-ink), 0.82);
}
.si-panel__eyebrow::before {
  content: "";
  width: 18px;
  height: 2px;
  border-radius: 2px;
  background: var(--tenant-tertiary);
}
.si-panel__headline {
  margin: 0 0 20px;
  font-size: clamp(40px, 5vw, 64px);
  line-height: 1.02;
  font-weight: 700;
  letter-spacing: -2px;
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.si-panel__lede {
  margin: 0;
  font-size: 18px;
  line-height: 1.6;
  color: rgba(var(--si-ink), 0.82);
}
.si-panel__foot {
  display: flex;
  flex-direction: column;
  gap: 28px;
  max-width: 460px;
}
.si-panel__glow {
  position: absolute;
  inset: auto -20% -30% auto;
  width: 70%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: radial-gradient(closest-side, var(--tenant-tertiary), transparent);
  opacity: 0.12;
  pointer-events: none;
}
.si-panel__inside {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.si-panel__inside-label {
  margin: 0;
  font-family: var(--si-mono);
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(var(--si-ink), 0.6);
}
.si-panel__features {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid rgba(var(--si-ink), 0.14);
}
.si-panel__feature {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid rgba(var(--si-ink), 0.14);
}
.si-panel__feature-icon {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(var(--si-ink), 0.08);
  border: 1px solid rgba(var(--si-ink), 0.16);
  color: var(--tenant-tertiary);
}
.si-panel__feature-icon :deep(.v-icon) {
  color: inherit;
}
.si-panel__feature-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.si-panel__feature-title {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
}
.si-panel__feature-text {
  font-size: 13px;
  line-height: 1.5;
  color: rgba(var(--si-ink), 0.72);
}
.si-panel__footnote {
  margin: 0;
  font-size: 12px;
  color: rgba(var(--si-ink), 0.55);
}

/* Motion only while the page says so (SignInSplit drops the class for reduced motion). */
.si-motion .si-panel__contour path {
  animation: si-contour 60s linear infinite;
}
.si-motion .si-panel__scan {
  animation: si-scan 7s cubic-bezier(0.6, 0, 0.4, 1) infinite;
}
@keyframes si-contour {
  to {
    stroke-dashoffset: -1200;
  }
}
@keyframes si-scan {
  from {
    transform: translateY(-10%);
  }
  to {
    transform: translateY(560%);
  }
}

/* Phone and small tablet: a short header band over the form. */
@media (max-width: 900px) {
  .si-panel {
    flex: 0 0 auto;
    min-height: 0;
    gap: 16px;
    padding: max(20px, env(safe-area-inset-top, 0px)) max(20px, env(safe-area-inset-right, 0px)) 24px
      max(20px, env(safe-area-inset-left, 0px));
  }
  .si-panel__eyebrow,
  .si-panel__lede,
  .si-panel__foot,
  .si-panel__glow,
  .si-panel__scene {
    display: none;
  }
  .si-panel__headline {
    margin: 0;
    font-size: 26px;
    letter-spacing: -0.6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

@media (prefers-reduced-motion: reduce) {
  .si-panel__contour path,
  .si-panel__scan {
    animation: none !important;
  }
}
</style>
